import React, { useCallback, useContext, useEffect, useState, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownConfigProps, CmOption, DropdownConfigValue } from './types';
import { createOption } from './utils';
import { OPTION_DEBOUNCE_MS } from './cm-control-config.constants';
import ConfirmPopup from '../../ModalPopup/confirm-popup';
import LibraryTitleInput from '../library-title-input';
import CustomTooltip from '../../Tooltip/CustomTooltip';
import { NewCheckbox } from '../../PIM/common/NewCheckbox';
import { NewInput } from '../../PIM/common/NewInput';
import OptionRow, { moveOption, useOptionReorder } from './option-row';
import { NewRadio } from '../../PIM/common/NewRadio';
import { NewSelectMultiSelect } from '../../PIM/common/NewSelectMultiSelect';
import type { NewSelectMultiSelectOption } from '../../PIM/common/NewSelectMultiSelect';
import FieldSetupPanelContext from './field-setup-panel.context';

function useDebouncedEmit<T>(onEmit: (value: T) => void, delayMs: number) {
  const valueRef = useRef<T>(null as unknown as T);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleEmit = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      onEmit(valueRef.current);
    }, delayMs);
  }, [onEmit, delayMs]);

  const onEmitRef = useRef(onEmit);
  onEmitRef.current = onEmit;

  const flushPending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      onEmitRef.current(valueRef.current);
    }
  }, []);

  useEffect(() => flushPending, [flushPending]);

  return { valueRef, scheduleEmit };
}

/**
 * DropdownConfig component for configuring dropdown form controls in the system canvas.
 * Allows users to set up dropdown options, required field settings, multi-select functionality,
 * and save configurations to library.
 * @param value - Current dropdown configuration value
 * @param onChange - Callback function called when configuration changes
 * @param className - Optional CSS class name for styling
 * @param disabled - Whether the configuration controls should be disabled
 * @param showLibraryTitle - Whether to show the library title input section
 */
const DropdownConfig: React.FC<DropdownConfigProps> = ({
  value,
  onChange,
  className = '',
  disabled = false,
  showLibraryTitle = true,
  width,
  onWidthChange,
  widthError,
  hideTitle = false,
}) => {
  const showWidthRow = onWidthChange != null;
  const { t } = useTranslation('custom-module-system-canvas');
  const titlePlaceholder = t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER');
  const setupPanel = useContext(FieldSetupPanelContext);
  const isInSetupCard = setupPanel !== null;
  const showSetupPart = setupPanel !== 'options';
  const showOptionsPart = setupPanel !== 'setup';

  const [localValue, setLocalValue] = useState<DropdownConfigValue>(value);
  const [libraryTitle, setLibraryTitle] = useState<string>('');
  const optionInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const justEmittedRef = useRef(false);

  const onEmit = useCallback(
    (v: DropdownConfigValue) => {
      justEmittedRef.current = true;
      onChange(v);
    },
    [onChange],
  );
  const { valueRef: latestValueRef, scheduleEmit: debouncedEmit } = useDebouncedEmit<DropdownConfigValue>(onEmit, OPTION_DEBOUNCE_MS);

  useEffect(() => {
    if (justEmittedRef.current) {
      justEmittedRef.current = false;
      latestValueRef.current = value;
      return;
    }
    setLocalValue(value);
    latestValueRef.current = value;
  }, [value, latestValueRef]);

  useEffect(() => {
    const lastIndex = localValue.options.length - 1;
    if (lastIndex < 0) return;
    const timer = setTimeout(() => {
      optionInputRefs.current[lastIndex]?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [localValue.options.length]);

  const updateTitle = useCallback(
    (next: string) => {
      setLocalValue((prev) => {
        const nextVal = { ...prev, title: next };
        latestValueRef.current = nextVal;
        onChange(nextVal);
        return nextVal;
      });
    },
    [onChange],
  );

  const toggleRequired = useCallback(() => {
    setLocalValue((prev) => {
      const nextVal = { ...prev, required: !prev.required };
      latestValueRef.current = nextVal;
      onChange(nextVal);
      return nextVal;
    });
  }, [onChange]);

  const toggleMulti = useCallback(() => {
    setLocalValue((prev) => {
      const nextVal = { ...prev, multiSelect: !prev.multiSelect };
      latestValueRef.current = nextVal;
      onChange(nextVal);
      return nextVal;
    });
  }, [onChange]);

  const addOption = useCallback(() => {
    const newOption = createOption('');
    justEmittedRef.current = true;
    setLocalValue((prev) => {
      const nextVal = { ...prev, options: [...prev.options, newOption] };
      latestValueRef.current = nextVal;
      onChange(nextVal);
      return nextVal;
    });
  }, [onChange]);

  const updateOptionAt = useCallback(
    (index: number, nextLabel: string) => {
      setLocalValue((prev) => {
        const nextOptions = prev.options.map((option, i) => (i === index ? { ...option, label: nextLabel } : option));
        const nextVal = { ...prev, options: nextOptions };
        latestValueRef.current = nextVal;
        return nextVal;
      });
      debouncedEmit();
    },
    [debouncedEmit],
  );

  const removeOptionAt = useCallback(
    (index: number) => {
      setLocalValue((prev) => {
        const nextOptions = prev.options.filter((_, i) => i !== index);
        optionInputRefs.current = optionInputRefs.current.filter((_, i) => i !== index);
        const nextVal = { ...prev, options: nextOptions.length ? nextOptions : [createOption('')] };
        latestValueRef.current = nextVal;
        onChange(nextVal);
        return nextVal;
      });
    },
    [onChange],
  );

  const [confirmIndex, setConfirmIndex] = useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);

  /**
   * Initiates the option removal confirmation process for a dropdown option
   * @param index - Index of the option to be removed
   */
  const requestRemoveOption = useCallback(
    (index: number) => {
      if (showConfirm) return;
      setConfirmIndex(index);
      setShowConfirm(true);
    },
    [showConfirm],
  );

  /**
   * Confirms and executes the removal of a dropdown option
   */
  const handleConfirmDelete = useCallback(() => {
    if (confirmIndex !== null) {
      removeOptionAt(confirmIndex);
    }
    setShowConfirm(false);
    setConfirmIndex(null);
  }, [confirmIndex, removeOptionAt]);

  /**
   * Cancels the option removal confirmation
   */
  const handleCancelDelete = useCallback(() => {
    setShowConfirm(false);
    setConfirmIndex(null);
  }, []);

  const handleLibrarySave = useCallback(() => {
    setLibraryTitle('');
  }, []);

  const handleLibrarySelect = useCallback(
    (controlTypeData: any) => {
      const fetchedConfig = controlTypeData?.content?.content as DropdownConfigValue | undefined;
      if (fetchedConfig) {
        setLocalValue(fetchedConfig);
        onChange(fetchedConfig);
      }
    },
    [onChange],
  );

  const moveOptionAt = useCallback(
    (fromIndex: number, toIndex: number) => {
      setLocalValue((prev) => {
        const nextVal = { ...prev, options: moveOption(prev.options, fromIndex, toIndex) };
        latestValueRef.current = nextVal;
        onChange(nextVal);
        return nextVal;
      });
    },
    [onChange],
  );

  const optionReorder = useOptionReorder(moveOptionAt);

  const typeOptions: NewSelectMultiSelectOption[] = [
    { value: DROPDOWN_TYPE_SINGLE, label: t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DROPDOWN.SINGLE_SELECT_DROPDOWN') },
    { value: DROPDOWN_TYPE_MULTI, label: t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DROPDOWN.MULTI_SELECT_DROPDOWN') },
  ];

  /**
   * Switches the dropdown between single and multi select from the setup card's Type select. A preset
   * default applies to single select only, so it is cleared when switching to multi select.
   * @param selected - The picked Type option.
   */
  const handleTypeChange = (selected: NewSelectMultiSelectOption | ReadonlyArray<NewSelectMultiSelectOption> | null) => {
    if (!selected || Array.isArray(selected)) return;
    const isMultiSelect = (selected as NewSelectMultiSelectOption).value === DROPDOWN_TYPE_MULTI;
    const nextVal = { ...localValue, multiSelect: isMultiSelect, value: isMultiSelect ? null : localValue.value ?? null };
    latestValueRef.current = nextVal;
    setLocalValue(nextVal);
    onChange(nextVal);
  };

  /**
   * Sets or clears a default option: one option for single select, any number for multi select.
   * @param optionLabel - Label of the option whose default control was clicked.
   */
  const togglePreset = (optionLabel: string) => {
    const current = localValue.value ?? null;
    const selected = Array.isArray(current) ? current : current ? [current] : [];
    const nextPreset = localValue.multiSelect
      ? selected.includes(optionLabel)
        ? selected.filter((label) => label !== optionLabel)
        : [...selected, optionLabel]
      : current === optionLabel
        ? null
        : optionLabel;
    const nextVal = { ...localValue, value: Array.isArray(nextPreset) && nextPreset.length === 0 ? null : nextPreset };
    latestValueRef.current = nextVal;
    setLocalValue(nextVal);
    onChange(nextVal);
  };

  return (
    <div className={`cm-maxw-520 ${className}`}>
      {showWidthRow && (
        <div className="mb-2">
          <label className="f-12 d-block">Width (%)</label>
          <NewInput
            type="text"
            placeholder="e.g. 25% or 150px"
            value={width ?? ''}
            onChange={(e) => onWidthChange(e.target.value.trim())}
            title="Field width when rendered (e.g. 25% or 150px)"
            invalid={!!widthError}
          />
          {widthError && <div className="invalid-feedback d-block">{widthError}</div>}
        </div>
      )}
      {showWidthRow && (
        <div className="mb-2">
          <p className="m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DROPDOWN.TITLE')}</p>
          <NewInput type="text" placeholder={titlePlaceholder} value={localValue.title} onChange={(e) => updateTitle(e.target.value)} />
        </div>
      )}
      {showLibraryTitle && (
        <LibraryTitleInput
          value={libraryTitle}
          onChange={setLibraryTitle}
          onSave={handleLibrarySave}
          onSelect={handleLibrarySelect}
          controlType="dropdown"
          contentData={localValue}
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER_LIBRARY_TITLE')}
        />
      )}
      {isInSetupCard && showSetupPart && (
        <div className="cm-field-setup__type">
          <label className="cm-field-setup__type-label">
            {t('CUSTOM_MODULE.SYSTEM_CANVAS.TYPE')}
            <span className="cm-field-required">*</span>
          </label>
          <NewSelectMultiSelect
            options={typeOptions}
            value={typeOptions.find((option) => option.value === (localValue.multiSelect ? DROPDOWN_TYPE_MULTI : DROPDOWN_TYPE_SINGLE))}
            onChange={handleTypeChange}
            isSearchable={false}
            isDisabled={disabled}
          />
        </div>
      )}
      {!isInSetupCard && (
      <div className="mb-1 mt-2 d-flex align-items-center cm-checkbox">
        <label className="m-0">
          {t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')}
          <span className="cm-field-required">*</span>
        </label>
        {!localValue.title?.trim() ? (
          <CustomTooltip tooltipText={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_CHECKBOX_TOOLTIP')}>
            <NewCheckbox className="mb-1 mr-0" checked={!!localValue.required} onChange={toggleRequired} disabled={disabled || !localValue.title?.trim()} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
          </CustomTooltip>
        ) : (
          <NewCheckbox className="mb-1 mr-0" checked={!!localValue.required} onChange={toggleRequired} disabled={disabled} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
        )}

        <label className="m-0 ml-3">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DROPDOWN.MULTI_SELECT')}</label>
        <NewCheckbox className="mb-1" checked={!!localValue.multiSelect} onChange={toggleMulti} disabled={disabled} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DROPDOWN.MULTI_SELECT')} />
      </div>
      )}
      {!showWidthRow && !hideTitle && (
        <div className="mb-2">
          <p className="m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DROPDOWN.TITLE')}</p>
          <NewInput type="text" placeholder={titlePlaceholder} value={localValue.title} onChange={(e) => updateTitle(e.target.value)} />
        </div>
      )}

      {showOptionsPart && (
      <div className={isInSetupCard ? '' : 'pt-2'}>
        {!isInSetupCard && (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <p className="m-0 font-weight-500">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DROPDOWN.LABEL')}</p>
            </div>
            <p className="mt-2 mb-1">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DROPDOWN.OPTIONS')}</p>
          </>
        )}
        <div>
          {localValue.options.map((option: CmOption, index: number) => (
            <OptionRow
              key={option.id || index}
              index={index}
              label={option.label}
              placeholder={titlePlaceholder}
              inputRef={(el) => {
                optionInputRefs.current[index] = el;
              }}
              onLabelChange={(nextLabel) => updateOptionAt(index, nextLabel)}
              isLast={index === localValue.options.length - 1}
              canDelete={localValue.options.length > 1}
              onAdd={addOption}
              onDelete={() => requestRemoveOption(index)}
              isDragOver={optionReorder.dragOverIndex === index}
              onReorderStart={optionReorder.onReorderStart}
              onReorderOver={optionReorder.onReorderOver}
              onReorderDrop={optionReorder.onReorderDrop}
              onReorderEnd={optionReorder.onReorderEnd}
              {...(isInSetupCard
                ? {
                    defaultControl: localValue.multiSelect ? (
                      <NewCheckbox
                        checked={Array.isArray(localValue.value) && localValue.value.includes(option.label)}
                        onChange={() => togglePreset(option.label)}
                        aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CHECKBOX.INCLUDE_IN_DEFAULT')}
                      />
                    ) : (
                      <NewRadio
                        name="dropdown-preset"
                        checked={(localValue.value ?? null) === option.label}
                        onClick={() => togglePreset(option.label)}
                        onChange={() => {}}
                        aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.RADIO.SET_AS_DEFAULT')}
                      />
                    ),
                  }
                : {})}
            />
          ))}
        </div>
      </div>
      )}
      <ConfirmPopup
        onOpen={showConfirm}
        title={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DROPDOWN.DELETE_OPTION_TITLE')}
        message={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DROPDOWN.DELETE_OPTION_MESSAGE')}
        toggle={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default memo(DropdownConfig);

const DROPDOWN_TYPE_SINGLE = 'single';

const DROPDOWN_TYPE_MULTI = 'multi';
